/**
 * API client for descriptor management
 */

import { apiFetch } from './client';

// --- Interfaces for list/dropdown usage ---

export interface Descriptor {
  descriptorId: number;
  namespace: string;
  codeValue: string;
  shortDescription: string;
  descriptorTypeId?: number | null;
  sortOrder?: number | null;
  active: boolean;
}

export interface DescriptorType {
  descriptorTypeId: number;
  descriptorTypeName: string;
  description?: string | null;
  sortOrder?: number | null;
  active: boolean;
}

// --- Interfaces for admin CRUD ---

export interface DescriptorDetail {
  descriptorId: number;
  namespace: string;
  codeValue: string;
  shortDescription: string;
  descriptorTypeId: number | null;
  description: string | null;
  effectiveBeginDate: string | null;
  effectiveEndDate: string | null;
  sortOrder: number | null;
  active: boolean;
  descriptorTypeName: string | null;
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface DescriptorCreateData {
  namespace: string;
  codeValue: string;
  shortDescription: string;
  descriptorTypeId?: number | null;
  description?: string | null;
  effectiveBeginDate?: string | null;
  effectiveEndDate?: string | null;
  sortOrder?: number | null;
  active?: boolean;
}

export interface DescriptorUpdateData {
  namespace?: string;
  codeValue?: string;
  shortDescription?: string;
  descriptorTypeId?: number | null;
  description?: string | null;
  effectiveBeginDate?: string | null;
  effectiveEndDate?: string | null;
  sortOrder?: number | null;
  active?: boolean;
}

export interface DescriptorTypeCreateData {
  descriptorTypeName: string;
  description?: string | null;
  sortOrder?: number | null;
  active?: boolean;
}

export interface DescriptorTypeUpdateData {
  descriptorTypeName?: string;
  description?: string | null;
  sortOrder?: number | null;
  active?: boolean;
}

class DescriptorsApi {
  /**
   * List descriptors with optional filtering
   */
  async list(params?: {
    namespace?: string;
    descriptorTypeId?: number;
    activeOnly?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<Descriptor[]> {
    const queryParams = new URLSearchParams();
    if (params?.namespace) queryParams.append('namespace', params.namespace);
    if (params?.descriptorTypeId !== undefined) {
      queryParams.append('descriptorTypeId', params.descriptorTypeId.toString());
    }
    if (params?.activeOnly !== undefined) {
      queryParams.append('activeOnly', params.activeOnly.toString());
    }
    if (params?.skip !== undefined) {
      queryParams.append('skip', params.skip.toString());
    }
    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }

    return await apiFetch<Descriptor[]>(
      `/descriptors/?${queryParams}`,
      { method: 'GET' }
    );
  }

  /**
   * Get all descriptor types
   */
  async getTypes(activeOnly?: boolean): Promise<DescriptorType[]> {
    const queryParams = new URLSearchParams();
    if (activeOnly !== undefined) {
      queryParams.append('activeOnly', activeOnly.toString());
    }
    return await apiFetch<DescriptorType[]>(
      `/descriptors/types?${queryParams}`,
      { method: 'GET' }
    );
  }

  /**
   * Get descriptors by type name
   */
  async getByTypeName(typeName: string): Promise<Descriptor[]> {
    const types = await this.getTypes();
    const type = types.find(t => t.descriptorTypeName === typeName);

    if (!type) {
      console.warn(`Descriptor type "${typeName}" not found`);
      return [];
    }

    return this.list({
      descriptorTypeId: type.descriptorTypeId,
      activeOnly: true
    });
  }

  // --- Admin CRUD for Descriptors ---

  /**
   * Get a single descriptor with full details
   */
  async getDescriptor(descriptorId: number): Promise<DescriptorDetail> {
    return await apiFetch<DescriptorDetail>(
      `/descriptors/${descriptorId}`,
      { method: 'GET' }
    );
  }

  /**
   * Create a new descriptor (superAdmin only)
   */
  async createDescriptor(data: DescriptorCreateData): Promise<DescriptorDetail> {
    return await apiFetch<DescriptorDetail>(
      `/descriptors/`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Update an existing descriptor (superAdmin only)
   */
  async updateDescriptor(descriptorId: number, data: DescriptorUpdateData): Promise<DescriptorDetail> {
    return await apiFetch<DescriptorDetail>(
      `/descriptors/${descriptorId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete a descriptor (superAdmin only)
   */
  async deleteDescriptor(descriptorId: number): Promise<void> {
    await apiFetch<null>(
      `/descriptors/${descriptorId}`,
      { method: 'DELETE' }
    );
  }

  // --- Admin CRUD for Descriptor Types ---

  /**
   * Create a new descriptor type (superAdmin only)
   */
  async createType(data: DescriptorTypeCreateData): Promise<DescriptorType> {
    return await apiFetch<DescriptorType>(
      `/descriptors/types`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Update a descriptor type (superAdmin only)
   */
  async updateType(descriptorTypeId: number, data: DescriptorTypeUpdateData): Promise<DescriptorType> {
    return await apiFetch<DescriptorType>(
      `/descriptors/types/${descriptorTypeId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete a descriptor type (superAdmin only)
   */
  async deleteType(descriptorTypeId: number): Promise<void> {
    await apiFetch<null>(
      `/descriptors/types/${descriptorTypeId}`,
      { method: 'DELETE' }
    );
  }
}

export const descriptorsApi = new DescriptorsApi();
